import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Loader2, Mail } from "lucide-react";
import { useToast } from "@/shared/ui/use-toast";
import { AuthLayout } from "./AuthLayout";
import { AuthCard, AuthCardHeader, AuthCardContent } from "./AuthCard";
import { LogoHeader, AuthHeader } from "./AuthHeader";
import { SuccessState } from "./SuccessState";
import { useForgotPassword } from "../hooks/auth.hooks";
const redirectTo = import.meta.env.VITE_APP_REDIRECT_PASSWORD_URL;



export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const { mutateAsync: forgotPassword, isPending: isLoading } = useForgotPassword({
    onSuccess: () => {
      setEmailSent(true);
      toast({
        title: "E-mail enviado!",
        description: "Verifique sua caixa de entrada para redefinir sua senha.",
        variant: "default",
      });

      // Redireciona após 3 segundos
      setTimeout(() => {
        navigate(redirectTo);
      }, 3000);
    },
    onError: () => {
      toast({
        title: "Erro",
        description: "Não foi possível enviar o e-mail de recuperação. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    forgotPassword({ email, redirectTo });
  };

  if (emailSent) {
    return (
      <AuthLayout>
        <AuthCard>
          <AuthCardHeader>
            <LogoHeader />
            <SuccessState
              title="Verifique seu e-mail"
              description={
                <>
                  Enviamos um link de recuperação para <span className="font-medium text-white">{email}</span>
                  <p className="mt-2 text-sm">Se você não encontrar o e-mail, verifique sua pasta de spam.</p>
                </>
              }
              buttonText="Voltar para o login"
              redirectTo="/auth"
              icon={<Mail className="h-8 w-8 text-green-500" />}
            />
          </AuthCardHeader>
        </AuthCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthCard>
        <AuthCardHeader>
          <LogoHeader />
          <AuthHeader
            title="Esqueceu sua senha?"
            description="Digite seu e-mail e enviaremos um link para redefinir sua senha."
          />
        </AuthCardHeader>
        <AuthCardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">
                E-mail
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-[#2A2A2A] border-white/[0.06] text-white"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-[#D91657] hover:bg-[#C1174F] text-white"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                "Enviar link de recuperação"
              )}
            </Button>
          </form>

          <div className="mt-4 text-center text-sm text-white/60">
            Lembrou sua senha?{" "}
            <Link
              to="/auth"
              className="text-[#D91657] hover:text-[#C1174F] hover:underline"
            >
              Faça login
            </Link>
          </div>
        </AuthCardContent>
      </AuthCard>
    </AuthLayout>
  );
}
